/**
 * Calendar Events Database Operations
 *
 * CRUD operations for Google Calendar events synced locally.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface CalendarEvent {
  id: string;
  connectionId: string;
  userId: string;
  orgId: string | null;
  googleEventId: string;
  googleCalendarId: string;
  summary: string | null;
  description: string | null;
  location: string | null;
  startTime: number; // ms
  endTime: number; // ms
  allDay: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  htmlLink: string | null;
  colorId: string | null;
  linkedTaskId: string | null;
  syncStatus: string;
  lastSyncedAt: string;
  createdAt: string;
}

// Supabase row type (snake_case)
interface CalendarEventRow {
  id: string;
  connection_id: string;
  user_id: string;
  org_id: string | null;
  google_event_id: string;
  google_calendar_id: string;
  summary: string | null;
  description: string | null;
  location: string | null;
  start_time: number;
  end_time: number;
  all_day: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  html_link: string | null;
  color_id: string | null;
  linked_task_id: string | null;
  sync_status: string;
  last_synced_at: string;
  created_at: string;
}

function convertEventFromSupabase(row: CalendarEventRow): CalendarEvent {
  return {
    id: row.id,
    connectionId: row.connection_id,
    userId: row.user_id,
    orgId: row.org_id,
    googleEventId: row.google_event_id,
    googleCalendarId: row.google_calendar_id,
    summary: row.summary,
    description: row.description,
    location: row.location,
    startTime: row.start_time,
    endTime: row.end_time,
    allDay: row.all_day,
    status: row.status,
    htmlLink: row.html_link,
    colorId: row.color_id,
    linkedTaskId: row.linked_task_id,
    syncStatus: row.sync_status,
    lastSyncedAt: row.last_synced_at,
    createdAt: row.created_at,
  };
}

/**
 * Fetch calendar events within a time range
 */
export async function fetchCalendarEvents(
  startTime: number,
  endTime: number
): Promise<CalendarEvent[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('google_calendar_events')
      .select('*')
      .gte('end_time', startTime)
      .lte('start_time', endTime)
      .neq('status', 'cancelled')
      .order('start_time', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertEventFromSupabase);
}

/**
 * Upsert (insert or update) calendar events from Google sync
 */
export async function upsertCalendarEvents(
  events: Array<{
    connectionId: string;
    userId: string;
    orgId: string | null;
    googleEventId: string;
    googleCalendarId: string;
    summary: string | null;
    description: string | null;
    location: string | null;
    startTime: number;
    endTime: number;
    allDay: boolean;
    status: 'confirmed' | 'tentative' | 'cancelled';
    htmlLink: string | null;
    colorId: string | null;
  }>
): Promise<CalendarEvent[]> {
  if (events.length === 0) return [];

  const rows = events.map((e) => ({
    connection_id: e.connectionId,
    user_id: e.userId,
    org_id: e.orgId,
    google_event_id: e.googleEventId,
    google_calendar_id: e.googleCalendarId,
    summary: e.summary,
    description: e.description,
    location: e.location,
    start_time: e.startTime,
    end_time: e.endTime,
    all_day: e.allDay,
    status: e.status,
    html_link: e.htmlLink,
    color_id: e.colorId,
    sync_status: 'synced',
    last_synced_at: new Date().toISOString(),
  }));

  const { data, error } = await withRetry(() =>
    supabase
      .from('google_calendar_events')
      .upsert(rows, { onConflict: 'connection_id,google_event_id' })
      .select()
  );

  if (error) throw error;
  return (data || []).map(convertEventFromSupabase);
}

/**
 * Link a calendar event to a Zenda task
 */
export async function linkEventToTask(
  eventId: string,
  taskId: string
): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('google_calendar_events')
      .update({ linked_task_id: taskId })
      .eq('id', eventId)
  );

  if (error) throw error;
}

/**
 * Unlink a calendar event from a Zenda task
 */
export async function unlinkEventFromTask(eventId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('google_calendar_events')
      .update({ linked_task_id: null })
      .eq('id', eventId)
  );

  if (error) throw error;
}
